import { useEffect, useRef, useState } from "react";
import useKine from "../hooks/useKine";
import { ReactTyped } from "react-typed";
import Button from "./Button";
import remarkGfm from "remark-gfm";
import Markdown from "react-markdown";
import { renderToString } from 'react-dom/server';
import { useNewApplicationContext } from "../providers/NewApplicationProvider";
import parse from 'html-react-parser';
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";


const stripSourceMarkers = (s: string) => s.replace(/【\d+:\d+†source】/g, "");

const KineResponse = ({ 
    markdown, 
    shouldBeTyped,
    setTyping
}: {
    markdown: string | null, 
    shouldBeTyped: boolean,
    isTyping: boolean,
    setTyping: (value: boolean) => void
}) => {

    if(!markdown) return <></>

    const strippedMarkdown = stripSourceMarkers(markdown);
    return (
      
        <div className='kine-response'>
            { shouldBeTyped ? 
                <ReactTyped
                    strings={[
                        renderToString(<Markdown children={strippedMarkdown} remarkPlugins={[remarkGfm]} />)
                    ]}
                    typeSpeed={.1}
                    backSpeed={.1}
                    showCursor={false} 
                    onComplete={() => setTyping(false)}
                >
                    
                </ReactTyped>
            :   <Markdown children={strippedMarkdown} remarkPlugins={[remarkGfm]} />
            }
        </div>
        
    )


    
}

const KineContainer = () => {
    const { evaluate, response, error } = useKine()
    const { formData, schema, section, form, sandbox } = useNewApplicationContext();
    const { t } = useTranslation();

    const [valid, setValid] = useState(false);
    const [title, setTitle] = useState(form?.tips?.title || 'Jeg er her for å hjelpe deg!');
    const [value, setValue] = useState(form?.tips?.value || 'Fyll ut skjemaet, så skal jeg hjelpe deg med å skrive søknadsteksten basert på det du har fylt inn så langt.');
    const [prompt, setPrompt] = useState<string | undefined>(undefined);

    const [shouldBeTyped, setShouldBeTyped] = useState(false);
    const [message, setMessage] = useState<string | null>(null)
    const [loading, setLoading] = useState(false);
    const [typing, setTyping] = useState(false)

    useEffect(() => {
        if(response){
            setTyping(shouldBeTyped);
            setMessage(response.map(r => r.content.map(c => c.text).join("\n")).join("\n"));
        }
    }, [response, message])
    
    useEffect(() => {
        setLoading(false);
        setTyping(false);
        setShouldBeTyped(false);
    }, [form])

     useEffect(() => {
        

        if(form?.tips){
            const { title, value } = form.tips;
            setTitle(title);
            setValue(value);
        }else{
            setTitle(null)
            setValue(null)
        }

       
        // Per-skjemadel-prompt: malen eier den (form-builder → «Iver-prompt»
        // på skjemaet, `portalApplicationTemplate`). Den gamle kilden
        // `openai_config.payload[type][form.url]` er migrert inn hit av
        // migrate_iver_prompts.php og leses ikke lenger.
        setPrompt(form?.assistantPrompt?.trim() || undefined);

        if(section?.type === 'summary'){
            setValid(true)
            setTitle('Oppsummert vurdering av søknaden');
            setValue('Hvis du ønsker, kan jeg gi deg en oppsummert tilbakemelding på hele søknaden basert på all informasjonen du har fylt inn.');
            setPrompt('Gi en kort oppsummert tilbakemelding av hele søknaden basert på all informasjon som er fylt inn.');
        }
    }, [form,section])

    useEffect(() => {
        currentValidation();
    }, [formData, form, section]);

    const currentValidation = () => {
        if (form && section) {
            const sectionSchema = schema?.shape[section.url];
            const formSchema = sectionSchema?.shape[form.url];

            if (sectionSchema && formSchema) {
                const response = formSchema.safeParse(formData);
                
                if (response.success) {
                    setValid(true);
                } else {
                    setValid(false);
                }
            }
        }
    };

    const onEvaluate = async () => {
        setShouldBeTyped(true)
        setLoading(true);
        try {
            await evaluate(prompt) 
        } catch (error) {
            console.log('onEvaluate error: ', error);
        } finally {
            setLoading(false);
        }   
    }

    const TipsMarkup = () => (
        <div className="tips">
            <h4 className="mb-3">{title ?? 'La meg vurdere'}</h4>
            <div className="small d-md-none mb-3">{value ? parse(value) : 'Lorem'}</div>
            <div className="d-none d-md-block mb-3">{value ? parse(value) : 'Fyll ut skjemaet over, så kan jeg vurdere innholdet'}</div>
        </div>
    )

    if(!title && !value && !prompt) return <></>

    
    return (
        <div className={`border rounded-2 p-4 ps-0 shadow`}>
            <div className="row g-0 align-items-start align-items-md-center">
                <div className="col-3 col-md-auto">
                    <Avatar
                        loading={loading}
                        typing={typing}
                    />
                </div>
                <div className="col">
                    <div className="kine-container">           
                        {loading &&
                            <ReactTyped strings={[
                                "Vent litt mens jeg tenker...", 
                                "Vent litt mens jeg analyserer...",
                                "Vent litt mens jeg formulerer meg...",
                            ]} typeSpeed={30} backSpeed={20} loop />
                        }
                        {response && prompt && !error && !loading &&
                            <KineResponse 
                                markdown={message} 
                                shouldBeTyped={shouldBeTyped}
                                isTyping={typing}
                                setTyping={setTyping}
                            /> 
                        }
                        {!response && !error && !loading && !typing && <TipsMarkup />}
                        {error  && !loading && 
                            <KineResponse 
                                markdown={`# Auda, noe gikk galt!\n\n${error.response?.data?.error || 'Noe gikk galt'}\n\n\`${error?.message} (${error?.status} ${error?.code})\`  \n\nPrøv igjen eller kontakt support.`} 
                                shouldBeTyped={shouldBeTyped}
                                isTyping={typing}
                                setTyping={setTyping}
                            /> 
                        }
                        
                        {valid && !loading && !typing && prompt &&
                            <div>
                                <Button
                                    title={!response ? "Evaluer med Iver" : error ? "Prøv igjen" : "Oppdater"}
                                    type="rainbow"
                                    iconWeight='fas'
                                    classes='btn-sm'
                                    leftIcon='sparkles'
                                    onClick={onEvaluate}
                                />
                            </div>
                        }

                        {sandbox &&
                            <p className="small text-warning-emphasis mt-3 mb-0">
                                <FontAwesomeIcon icon={['fal', 'flask']} className="me-2" />
                                {t('playground_iver_note')}
                            </p>
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

export const Avatar = ({ loading, typing, maxWidth = 200 }: { loading: boolean, typing: boolean, maxWidth?: number}) => {

    let timer: ReturnType<typeof setTimeout>;
    const [state, setState] = useState<'idle' | 'thinking' | 'talking' | 'special' | null>(null);
    const [specialIterator, setSpecialIterator] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);



    const onVideoEnd = () => {
        setState(null);
    }

    useEffect(() => {
        if(loading) setState('thinking')
        if(typing) setState('talking')
        if(!loading && !typing) setState(null)
    }, [loading, typing])

    useEffect(() => {
        if(state !== null){
            clearTimeout(timer)
            return;
        }
        const delay = 5000 + Math.random() * 5000;
        timer = setTimeout(() => {
            setState(specialIterator === 4 ? 'special' : 'idle')
        }, delay)

        return () => {
            clearTimeout(timer)
        }
    }, [state])

    

    useEffect(() => {
        if(videoRef.current){
            const video = videoRef.current;
            video.loop = state === 'thinking' || state === 'talking';
        }
        if(state === 'idle' || state === 'special'){
            const next = (specialIterator + 1) % 5;
            setSpecialIterator(next);
        }
    }, [state])   

    return (
        <>
        <div className="aa-avatar-wrapper" style={{position: 'relative', width: '100%', height: 'auto'}}>
            <img src={`${import.meta.env.BASE_URL}ai-assistant/ai-bot.png`} style={{maxWidth: maxWidth}} />
            {state &&
                <video
                    ref={videoRef}
                    className="d-"
                    style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}}
                    autoPlay
                    muted
                    playsInline
                    onEnded={() => onVideoEnd()}
                    onPause={() => onVideoEnd()}
                    src={`${import.meta.env.BASE_URL}ai-assistant/ai-bot-${state}.mp4`}
                />
            }
            
        </div>
        </>
    )
}

export default KineContainer;